/**
 * Utilidades para comparación de priors (P-COMPARE-PRIORS).
 * Merge de stacks de dos estudios en el store sin reemplazar el actual.
 * Agrupación Explorer: StudyInstanceUID + SeriesInstanceUID (no date/SN solos).
 */

import { groupBy } from '../functions'

/**
 * @param {object|null|undefined} file
 * @returns {string}
 */
export function studyUidFromFile(file) {
  if (!file || typeof file !== 'object') return ''
  const st = file.study || {}
  return String(
    st.studyId ||
      st.studyInstanceUID ||
      file.studyInstanceUID ||
      file.study_uid ||
      ''
  ).trim()
}

/**
 * Clave estable de estudio para Explorer (evita fusionar priors por fecha).
 * @param {object|null|undefined} file
 * @returns {string}
 */
export function studyGroupKey(file) {
  const uid = studyUidFromFile(file)
  if (uid) return `uid:${uid}`
  const st = file && file.study ? file.study : {}
  const dt =
    st.studyDateTime != null ? String(st.studyDateTime).trim() : ''
  if (dt) return `dt:${dt}`
  const date = st.studyDate != null ? String(st.studyDate).trim() : ''
  if (date) return `d:${date}`
  const desc =
    st.studyDescription != null ? String(st.studyDescription).trim() : ''
  if (desc) return `desc:${desc}`
  return 'unknown'
}

/**
 * Clave estable de serie (SeriesInstanceUID; fallback SN scoped por StudyUID).
 * @param {object|null|undefined} file
 * @returns {string}
 */
export function seriesGroupKey(file) {
  if (!file || typeof file !== 'object') return 'unknown'
  const su =
    (file.series && file.series.seriesInstanceUID) ||
    file.seriesInstanceUID ||
    ''
  const s = String(su).trim()
  if (s) return `suid:${s}`
  const sn =
    file.series && file.series.seriesNumber != null
      ? file.series.seriesNumber
      : 0
  const stu = studyUidFromFile(file) || 'x'
  return `sn:${stu}:${sn}`
}

/**
 * @param {string|null|undefined} dcmDate YYYYMMDD
 * @returns {string}
 */
export function formatStudyDateLabel(dcmDate) {
  const s = String(dcmDate || '')
  if (s.length >= 8) {
    return `${s.slice(6, 8)}-${s.slice(4, 6)}-${s.slice(0, 4)}`
  }
  return s || ''
}

/**
 * Conteos de series/imágenes de un bucket de estudio.
 * @param {object[]|null|undefined} files
 * @returns {{ seriesCount: number, instanceCount: number, series: { key: string, count: number, seriesNumber: *, description: string }[] }}
 */
export function studySeriesStats(files) {
  const list = Array.isArray(files) ? files.filter(Boolean) : []
  if (!list.length) {
    return { seriesCount: 0, instanceCount: 0, series: [] }
  }
  const { seriesList, seriesKeys } = buildSeriesMapForStudy(list)
  const series = seriesKeys.map((k) => {
    const arr = seriesList.get(k) || []
    const first = arr[0] || {}
    const sn =
      first.series && first.series.seriesNumber != null
        ? first.series.seriesNumber
        : null
    const description =
      (first.series && first.series.seriesDescription) || ''
    return {
      key: k,
      count: arr.length,
      seriesNumber: sn,
      description: String(description || '').trim(),
    }
  })
  return {
    seriesCount: seriesKeys.length,
    instanceCount: list.length,
    series,
  }
}

/**
 * @param {{ seriesCount?: number, instanceCount?: number, series?: { count?: number }[] }|null|undefined} stats
 * @returns {string}
 */
export function formatStudyStatsLine(stats) {
  if (!stats) return ''
  const sc = Number(stats.seriesCount) || 0
  const ic = Number(stats.instanceCount) || 0
  const counts = Array.isArray(stats.series)
    ? stats.series.map((s) => s && s.count).filter((n) => n > 0)
    : []
  if (sc > 0 && counts.length > 0) {
    return `${sc} series (${counts.join(', ')})`
  }
  if (sc > 0 && ic > 0) return `${sc} series · ${ic} img`
  if (ic > 0) return `${ic} img`
  if (sc > 0) return `${sc} series`
  return ''
}

/**
 * Label legible para el dropdown Study (value = studyGroupKey).
 * @param {object[]|null|undefined} files
 * @param {{ inViewport?: boolean }} [opts]
 * @returns {string}
 */
export function studyDropdownLabel(files, opts = {}) {
  const list = Array.isArray(files) ? files : []
  const f = list.find(Boolean) || {}
  const st = f.study || {}
  const date =
    formatStudyDateLabel(st.studyDate) ||
    (st.studyDateTime != null
      ? String(st.studyDateTime).trim().slice(0, 16)
      : '')
  const mod =
    st.modalitiesInStudy ||
    (f.series && f.series.modality) ||
    f.modality ||
    ''
  const desc = st.studyDescription ? String(st.studyDescription).trim() : ''
  const parts = [date, mod, desc].filter(Boolean)
  const statsLine = formatStudyStatsLine(studySeriesStats(list))
  if (statsLine) parts.push(statsLine)
  if (opts && opts.inViewport) parts.push('en vista')
  if (parts.length) return parts.join(' · ')
  const uid = studyUidFromFile(f)
  return uid ? `Estudio ${uid.slice(0, 12)}…` : 'Estudio'
}

/**
 * @param {Map<string, object[]>} studyList
 * @returns {string[]}
 */
export function sortStudyKeys(studyList) {
  const keys = [...(studyList || new Map()).keys()].filter(
    (k) => k != null && String(k) !== ''
  )
  keys.sort((a, b) => {
    const fa = (studyList.get(a) || [])[0]
    const fb = (studyList.get(b) || [])[0]
    const da = (fa && fa.study && fa.study.studyDate) || ''
    const db = (fb && fb.study && fb.study.studyDate) || ''
    if (da !== db) return String(db).localeCompare(String(da))
    return String(a).localeCompare(String(b))
  })
  return keys
}

/**
 * @param {Map<string, object[]>} seriesList
 * @returns {string[]}
 */
function isReportSeriesFile(file) {
  if (!file || typeof file !== 'object') return false
  const role = file.role || (file.series && file.series.role)
  if (role === 'report') return true
  const mod = String(
    (file.series && (file.series.modality || file.series.Modality)) ||
      file.modality ||
      ''
  )
    .trim()
    .toUpperCase()
  return mod === 'DOC' || mod === 'PDF'
}

export function sortSeriesKeys(seriesList) {
  const keys = [...(seriesList || new Map()).keys()]
  keys.sort((a, b) => {
    const fa = (seriesList.get(a) || [])[0]
    const fb = (seriesList.get(b) || [])[0]
    const ra = isReportSeriesFile(fa)
    const rb = isReportSeriesFile(fb)
    if (ra !== rb) return ra ? 1 : -1
    const sna =
      fa && fa.series && fa.series.seriesNumber != null
        ? Number(fa.series.seriesNumber)
        : 0
    const snb =
      fb && fb.series && fb.series.seriesNumber != null
        ? Number(fb.series.seriesNumber)
        : 0
    if (sna !== snb) return sna - snb
    return String(a).localeCompare(String(b))
  })
  return keys
}

/**
 * Mapas study/series para un paciente (keys UID-safe).
 * @param {object[]} patientFiles
 * @returns {{ studyList: Map, studyKeys: string[], studyLabels: Object<string,string> }}
 */
export function buildStudySeriesMaps(patientFiles) {
  const studyList = groupBy(patientFiles || [], studyGroupKey)
  const studyKeys = sortStudyKeys(studyList)
  const studyLabels = {}
  for (let i = 0; i < studyKeys.length; i++) {
    const k = studyKeys[i]
    studyLabels[k] = studyDropdownLabel(studyList.get(k))
  }
  return { studyList, studyKeys, studyLabels }
}

/**
 * Series de un study bucket, ordenadas por SeriesNumber.
 * @param {object[]} studyFiles
 * @returns {{ seriesList: Map, seriesKeys: string[] }}
 */
export function buildSeriesMapForStudy(studyFiles) {
  const raw = groupBy(studyFiles || [], seriesGroupKey)
  const seriesKeys = sortSeriesKeys(raw)
  const seriesList = new Map()
  for (let i = 0; i < seriesKeys.length; i++) {
    const k = seriesKeys[i]
    seriesList.set(k, raw.get(k) || [])
  }
  return { seriesList, seriesKeys }
}

/**
 * @param {object|null|undefined} file
 * @returns {string}
 */
export function fileInstanceKey(file) {
  if (!file || typeof file !== 'object') return ''
  const sop =
    file.sopInstanceUID != null ? String(file.sopInstanceUID).trim() : ''
  if (sop) return `sop:${sop}`
  const imageId = file.imageId != null ? String(file.imageId).trim() : ''
  if (imageId) return `img:${imageId}`
  return ''
}

/**
 * Concatena base + incoming sin duplicar instancias.
 * @param {object[]} base
 * @param {object[]} incoming
 * @returns {object[]}
 */
export function mergeStudyFileLists(base, incoming) {
  const out = []
  const seen = new Set()
  const push = (f) => {
    if (!f) return
    const k = fileInstanceKey(f)
    if (k) {
      if (seen.has(k)) return
      seen.add(k)
    }
    out.push(f)
  }
  const a = Array.isArray(base) ? base : []
  const b = Array.isArray(incoming) ? incoming : []
  for (let i = 0; i < a.length; i++) push(a[i])
  for (let j = 0; j < b.length; j++) push(b[j])
  return out
}

/**
 * Explorer data desde lista de files (mismo shape que loadStudyFromManifest).
 * @param {object[]} items
 */
export function buildExplorerDataFromFiles(items) {
  const list = Array.isArray(items) ? items : []
  const patientList = groupBy(list, (a) =>
    a && a.patient && a.patient.patientName
      ? a.patient.patientName
      : 'Unknown'
  )
  return {
    folder: null,
    patient: {
      list: patientList,
      keys: [...patientList.keys()],
    },
  }
}

/**
 * Primera serie “displayable” de un StudyInstanceUID dentro del pool.
 * Agrupa por SeriesInstanceUID (no solo SeriesNumber).
 * @param {object[]} files
 * @param {string} studyUid
 * @returns {{ seriesFiles: object[], explorerHint: number }|null}
 */
export function pickFirstSeriesForStudy(files, studyUid) {
  const want = String(studyUid || '').trim()
  if (!want || !Array.isArray(files) || !files.length) return null
  const ofStudy = files.filter((f) => studyUidFromFile(f) === want)
  if (!ofStudy.length) return null

  const { seriesList, seriesKeys } = buildSeriesMapForStudy(ofStudy)
  for (let i = 0; i < seriesKeys.length; i++) {
    const cand = seriesList.get(seriesKeys[i]) || []
    if (cand.some((f) => f && (f.image || f.imageId))) {
      return { seriesFiles: cand, explorerHint: i }
    }
  }
  const firstKey = seriesKeys[0]
  const first = firstKey != null ? seriesList.get(firstKey) : ofStudy
  return { seriesFiles: first || ofStudy, explorerHint: 0 }
}

/** Score mínimo para badge «Sugerida» (sync, sin red). */
export const PRIOR_MATCH_MIN_SCORE = 30
/** Score mínimo para auto-abrir serie prior al entrar en Comparar. */
export const PRIOR_MATCH_AUTO_OPEN_MIN_SCORE = 50

const STOP_TOKENS = new Set([
  'the',
  'and',
  'for',
  'with',
  'sin',
  'con',
  'de',
  'del',
  'la',
  'el',
  'los',
  'las',
  'a',
  'en',
  'se',
  'mm',
  'cm',
  'kv',
  'ma',
  'msec',
  'sec',
])

/**
 * @param {number[]|null|undefined} iop
 * @returns {number[]|null} normal unitaria del plano
 */
export function planeNormalFromIop(iop) {
  if (!Array.isArray(iop) || iop.length < 6) return null
  const r = [Number(iop[0]), Number(iop[1]), Number(iop[2])]
  const c = [Number(iop[3]), Number(iop[4]), Number(iop[5])]
  if (r.some((n) => !Number.isFinite(n)) || c.some((n) => !Number.isFinite(n))) {
    return null
  }
  const n = [
    r[1] * c[2] - r[2] * c[1],
    r[2] * c[0] - r[0] * c[2],
    r[0] * c[1] - r[1] * c[0],
  ]
  const len = Math.sqrt(n[0] * n[0] + n[1] * n[1] + n[2] * n[2])
  if (!Number.isFinite(len) || len < 1e-6) return null
  return [n[0] / len, n[1] / len, n[2] / len]
}

/**
 * @param {string|null|undefined} text
 * @returns {string[]}
 */
export function tokenizeSeriesDescription(text) {
  const raw = String(text || '')
    .toLowerCase()
    .replace(/[^a-z0-9áéíóúñü.+]+/gi, ' ')
  const parts = raw.split(/\s+/).filter(Boolean)
  const out = []
  const seen = new Set()
  for (let i = 0; i < parts.length; i++) {
    let t = parts[i].replace(/^\.+|\.+$/g, '')
    if (t.length < 2) continue
    if (STOP_TOKENS.has(t)) continue
    if (/^\d+(\.\d+)?$/.test(t)) continue
    if (seen.has(t)) continue
    seen.add(t)
    out.push(t)
  }
  return out
}

/**
 * Meta liviana de una serie (1er frame útil). Sync, sin red.
 * @param {object[]|null|undefined} files
 * @returns {{
 *   seriesKey: string,
 *   studyUid: string,
 *   modality: string,
 *   seriesDescription: string,
 *   seriesNumber: number|null,
 *   isScout: boolean,
 *   isReport: boolean,
 *   imageOrientationPatient: number[]|null,
 *   sample: object|null,
 * }|null}
 */
export function seriesMetaFromFiles(files) {
  const list = Array.isArray(files) ? files.filter(Boolean) : []
  if (!list.length) return null
  const sample =
    list.find((f) => f && (f.imageOrientationPatient || f.iop)) || list[0]
  const series = (sample && sample.series) || {}
  const modality = String(
    series.modality || series.Modality || sample.modality || ''
  )
    .trim()
    .toUpperCase()
  const seriesDescription = String(
    series.seriesDescription || series.description || sample.seriesDescription || ''
  ).trim()
  let iop = null
  const rawIop =
    sample.imageOrientationPatient ||
    sample.iop ||
    sample.ImageOrientationPatient ||
    null
  if (Array.isArray(rawIop) && rawIop.length >= 6) {
    iop = rawIop.slice(0, 6).map(Number)
    if (iop.some((n) => !Number.isFinite(n))) iop = null
  }
  if (!iop) {
    for (let i = 0; i < list.length && i < 8; i++) {
      const f = list[i]
      const v =
        f &&
        (f.imageOrientationPatient || f.iop || f.ImageOrientationPatient)
      if (Array.isArray(v) && v.length >= 6) {
        const nums = v.slice(0, 6).map(Number)
        if (nums.every((n) => Number.isFinite(n))) {
          iop = nums
          break
        }
      }
    }
  }
  const isReport =
    series.role === 'report' ||
    modality === 'DOC' ||
    modality === 'PDF' ||
    modality === 'SR' ||
    modality === 'PR'
  const isScout =
    series.isScout === true ||
    /scout|localizer|locator|surview|topogram|scanogram|pilot/i.test(
      seriesDescription
    )
  return {
    seriesKey: seriesGroupKey(sample),
    studyUid: studyUidFromFile(sample),
    modality,
    seriesDescription,
    seriesNumber:
      series.seriesNumber != null ? Number(series.seriesNumber) : null,
    isScout,
    isReport,
    imageOrientationPatient: iop,
    sample,
  }
}

/**
 * Score 0–100 aprox. entre serie activa y candidata del prior.
 * Solo metadatos en memoria (modality, description, IOP opcional).
 * @param {ReturnType<typeof seriesMetaFromFiles>|null} active
 * @param {ReturnType<typeof seriesMetaFromFiles>|null} candidate
 * @returns {{ score: number, reasons: string[] }}
 */
export function scorePriorSeries(active, candidate) {
  const reasons = []
  if (!active || !candidate) return { score: 0, reasons }
  if (candidate.isReport) {
    return { score: -100, reasons: ['report'] }
  }

  let score = 0
  const aMod = active.modality
  const cMod = candidate.modality
  if (aMod && cMod && aMod === cMod) {
    score += 40
    reasons.push('modality')
  } else if (aMod && cMod) {
    score -= 15
    reasons.push('modality_mismatch')
  }

  const aTok = tokenizeSeriesDescription(active.seriesDescription)
  const cTok = tokenizeSeriesDescription(candidate.seriesDescription)
  if (aTok.length && cTok.length) {
    const cSet = new Set(cTok)
    let shared = 0
    for (let i = 0; i < aTok.length; i++) {
      if (cSet.has(aTok[i])) shared += 1
    }
    const union = new Set(aTok.concat(cTok)).size || 1
    const jaccard = shared / union
    const descPts = Math.round(jaccard * 40)
    if (descPts > 0) {
      score += descPts
      reasons.push(`desc:${shared}/${union}`)
    }
  }

  const nA = planeNormalFromIop(active.imageOrientationPatient)
  const nC = planeNormalFromIop(candidate.imageOrientationPatient)
  if (nA && nC) {
    const dot = Math.abs(nA[0] * nC[0] + nA[1] * nC[1] + nA[2] * nC[2])
    const planePts = Math.round(Math.min(1, dot) * 25)
    if (planePts > 0) {
      score += planePts
      reasons.push(`plane:${planePts}`)
    }
  }

  if (candidate.isScout) {
    score -= 25
    reasons.push('scout')
  }
  if (active.isScout && candidate.isScout) {
    score += 10
    reasons.push('scout_pair')
  }

  if (
    active.seriesNumber != null &&
    candidate.seriesNumber != null &&
    Number(active.seriesNumber) === Number(candidate.seriesNumber)
  ) {
    score += 5
    reasons.push('series_number')
  }

  return { score, reasons }
}

/**
 * Ranking de series de un estudio prior vs la serie activa.
 * @param {object[]|null|undefined} activeFiles
 * @param {object[]|null|undefined} priorStudyFiles
 * @returns {Array<{
 *   seriesKey: string,
 *   score: number,
 *   reasons: string[],
 *   seriesFiles: object[],
 *   meta: ReturnType<typeof seriesMetaFromFiles>,
 *   explorerHint: number,
 * }>}
 */
export function rankPriorSeries(activeFiles, priorStudyFiles) {
  const activeMeta = seriesMetaFromFiles(activeFiles)
  if (!activeMeta) return []
  const { seriesList, seriesKeys } = buildSeriesMapForStudy(priorStudyFiles)
  const ranked = []
  for (let i = 0; i < seriesKeys.length; i++) {
    const key = seriesKeys[i]
    const seriesFiles = seriesList.get(key) || []
    const meta = seriesMetaFromFiles(seriesFiles)
    if (!meta || meta.isReport) continue
    const { score, reasons } = scorePriorSeries(activeMeta, meta)
    ranked.push({
      seriesKey: key,
      score,
      reasons,
      seriesFiles,
      meta,
      explorerHint: i,
    })
  }
  ranked.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score
    return String(a.seriesKey).localeCompare(String(b.seriesKey))
  })
  return ranked
}

/**
 * Mejor serie prior (otro StudyUID) para la serie activa.
 * @param {object[]|null|undefined} activeFiles
 * @param {object[]|null|undefined} allFiles
 * @param {{
 *   primaryStudyUid?: string|null,
 *   priorStudyUid?: string|null,
 *   minScore?: number,
 * }} [opts]
 * @returns {{
 *   seriesKey: string,
 *   score: number,
 *   reasons: string[],
 *   seriesFiles: object[],
 *   priorStudyUid: string,
 *   explorerHint: number,
 * }|null}
 */
export function suggestPriorSeries(activeFiles, allFiles, opts = {}) {
  const activeMeta = seriesMetaFromFiles(activeFiles)
  if (!activeMeta) return null
  const activeUid = activeMeta.studyUid
  const primary =
    opts.primaryStudyUid != null
      ? String(opts.primaryStudyUid).trim()
      : activeUid
  const forcedPrior =
    opts.priorStudyUid != null ? String(opts.priorStudyUid).trim() : ''
  const minScore =
    opts.minScore != null ? Number(opts.minScore) : PRIOR_MATCH_MIN_SCORE

  const pool = Array.isArray(allFiles) ? allFiles.filter(Boolean) : []
  if (!pool.length) return null

  const byStudy = new Map()
  for (let i = 0; i < pool.length; i++) {
    const f = pool[i]
    const uid = studyUidFromFile(f)
    if (!uid) continue
    if (forcedPrior) {
      if (uid !== forcedPrior) continue
    } else if (primary && uid === primary) {
      continue
    } else if (activeUid && uid === activeUid) {
      continue
    }
    if (!byStudy.has(uid)) byStudy.set(uid, [])
    byStudy.get(uid).push(f)
  }

  const activeKey = activeMeta.seriesKey || ''
  let best = null
  for (const [priorUid, studyFiles] of byStudy) {
    if (!priorUid) continue
    if (primary && priorUid === primary) continue
    if (activeUid && priorUid === activeUid) continue
    const ranked = rankPriorSeries(activeFiles, studyFiles)
    const top = ranked[0]
    if (!top || top.score < minScore) continue
    // Nunca sugerir la misma serie que la referencia
    if (activeKey && top.seriesKey === activeKey) continue
    const topUid =
      (top.meta && top.meta.studyUid) ||
      studyUidFromFile(
        Array.isArray(top.seriesFiles) && top.seriesFiles[0]
          ? top.seriesFiles[0]
          : null
      ) ||
      priorUid
    if (!topUid || topUid === primary || topUid === activeUid) continue
    if (!best || top.score > best.score) {
      best = {
        seriesKey: top.seriesKey,
        score: top.score,
        reasons: top.reasons,
        seriesFiles: top.seriesFiles,
        priorStudyUid: priorUid,
        explorerHint: top.explorerHint,
      }
    }
  }
  return best
}
